import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Select from "../../../components/Form/Select";
import TextField from "../../../components/Form/TextField";
import Button from "../../../components/Buttons/Button";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  startListenerMergedFonts,
  startUploadAdvertiserFont,
  stopListenerMergedFonts,
} from "../../../actions/fonts";
import {
  ADD_FONT_VALUE,
  getFontMimeType,
  loadFontFaces,
  normalizeFontName,
  validateFontName,
} from "../utils/fonts";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  FIELD_REQUIRED,
  TEMPLATES_ADD_FONT,
  TEMPLATES_ADD_FONT_ERROR,
  TEMPLATES_ADD_FONT_FILE,
  TEMPLATES_ADD_FONT_INVALID_FILE,
  TEMPLATES_ADD_FONT_INVALID_NAME,
  TEMPLATES_ADD_FONT_NAME,
  TEMPLATES_ADD_FONT_NAME_HINT,
  TEMPLATES_ADD_FONT_SELECT_FILE,
  TEMPLATES_ADD_FONT_TITLE,
  TEMPLATES_FONT_FAMILY,
  TEMPLATES_UNAUTHORIZED,
} from "../../../i18n/keysTranslations";

const optionalRules = { required: false };

const AddFontModal = ({ modalOpen, disabled, onCloseModal, onUploaded }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (!modalOpen) return;
    reset({ name: "" });
    setFile(null);
  }, [modalOpen, reset]);

  const onClose = () => {
    if (isUploading) return;
    onCloseModal();
  };

  const onSubmit = async ({ name }) => {
    const fontName = normalizeFontName({ name });
    const nameError = validateFontName({ name: fontName });
    if (nameError === "required") {
      setError("name", { type: "required", message: t(FIELD_REQUIRED) });
      return;
    }
    if (nameError === "invalidName") {
      setError("name", {
        type: "validate",
        message: t(TEMPLATES_ADD_FONT_INVALID_NAME),
      });
      return;
    }
    if (!getFontMimeType({ file })) {
      SimpleAlert({
        title: t(TEMPLATES_ADD_FONT_INVALID_FILE),
        icon: "error",
      });
      return;
    }

    setIsUploading(true);
    const response = await dispatch(
      startUploadAdvertiserFont({ name: fontName, file })
    );
    setIsUploading(false);

    if (!response.ok) {
      SimpleAlert({
        title: t(
          response.reason === "unauthorized"
            ? TEMPLATES_UNAUTHORIZED
            : response.reason === "invalidFile"
              ? TEMPLATES_ADD_FONT_INVALID_FILE
              : response.reason === "invalidName"
                ? TEMPLATES_ADD_FONT_INVALID_NAME
                : TEMPLATES_ADD_FONT_ERROR
        ),
        icon: "error",
      });
      return;
    }

    onUploaded(response.data);
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      title={t(TEMPLATES_ADD_FONT_TITLE)}
      ariaLabel={t(TEMPLATES_ADD_FONT_TITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          name="name"
          control={control}
          errors={errors}
          label={t(TEMPLATES_ADD_FONT_NAME)}
          variant="filled"
          fullWidth
          autoFocus
          disabled={disabled || isUploading}
          helperText={t(TEMPLATES_ADD_FONT_NAME_HINT)}
          rules={{
            required: { value: true, message: t(FIELD_REQUIRED) },
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
          >
            {t(TEMPLATES_ADD_FONT_FILE)}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            hidden
            onChange={(event) => {
              const nextFile = event.target.files?.[0] || null;
              event.target.value = "";
              setFile(nextFile);
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              color="secondary"
              disabled={disabled || isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {t(TEMPLATES_ADD_FONT_SELECT_FILE)}
            </Button>
            {file?.name ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {file.name}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
      <DialogActions
        sx={{
          px: { xs: 3, sm: 5 },
          pb: 3,
          mt: 0.5,
        }}
      >
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onClose,
            disabled: isUploading,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            onClick: handleSubmit(onSubmit),
            loading: isUploading,
            disabled: disabled || isUploading,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

const FontFamilySelect = ({
  control,
  errors,
  disabled = false,
  name = "fontAssetRef",
  onChangeField,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const listenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const [fonts, setFonts] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);

  useEffect(() => {
    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (nextFonts) => {
          setFonts(nextFonts);
          loadFontFaces({ fonts: nextFonts });
        },
        onReferenceAvailable: (refs) => {
          listenerRefs.current = refs;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerMergedFonts({ listenerRefs: listenerRefs.current })
      );
    };
  }, [dispatch]);

  const currentValue = useWatch({ control, name }) || "";

  const options = useMemo(() => {
    const fontOptions = fonts.map((font) => ({
      value: font.name,
      label: (
        <Box component="span" sx={{ fontFamily: `"${font.name}"` }}>
          {font.name}
        </Box>
      ),
    }));
    if (
      currentValue &&
      currentValue !== ADD_FONT_VALUE &&
      !fontOptions.some((option) => option.value === currentValue)
    ) {
      fontOptions.unshift({
        value: currentValue,
        label: (
          <Box component="span" sx={{ fontFamily: `"${currentValue}"` }}>
            {currentValue}
          </Box>
        ),
      });
    }
    return [
      { value: ADD_FONT_VALUE, label: t(TEMPLATES_ADD_FONT) },
      ...fontOptions,
    ];
  }, [currentValue, fonts, t]);

  return (
    <>
      <Select
        control={control}
        errors={errors}
        name={name}
        label={t(TEMPLATES_FONT_FAMILY)}
        variant="filled"
        fullWidth
        displayEmpty
        disabled={disabled}
        rules={optionalRules}
        options={options}
        renderValue={(selected) => {
          if (!selected || selected === ADD_FONT_VALUE) return "";
          return (
            <Box component="span" sx={{ fontFamily: `"${selected}"` }}>
              {selected}
            </Box>
          );
        }}
        validationChange={(value) => {
          if (value !== ADD_FONT_VALUE) return true;
          if (!disabled) setIsAddOpen(true);
          return false;
        }}
        actionsChange={(value) =>
          onChangeField({ key: "fontAssetRef", value })
        }
      />
      <AddFontModal
        modalOpen={isAddOpen}
        disabled={disabled}
        onCloseModal={() => setIsAddOpen(false)}
        onUploaded={(font) => {
          loadFontFaces({ fonts: [font] });
          onChangeField({ key: "fontAssetRef", value: font.name });
        }}
      />
    </>
  );
};

export default FontFamilySelect;
